import React from 'react'
import type { MouseEvent } from 'react'
import { createElement, useCallback, useMemo, useState } from 'react'
import { useTooltip } from '@nivo/tooltip'
import type { FC } from 'react'
import { CHART_COLORS } from '../../helpers/settings'

interface IProps {
  dataProps: any
  layerProps: any
}

const BenchmarkHorizontalLayer: FC<IProps> = ({ dataProps, layerProps }) => {
  const [tooltipValue, setTooltipValue] = useState<number | null>(null)
  const { showTooltipFromEvent, hideTooltip } = useTooltip()

  const {
    isInteractive,
    onMouseEnter,
    onMouseLeave,

    tooltip,
  } = layerProps

  const data: any = {
    formattedValue: tooltipValue || '0',
    hidden: false,
    id: 'Benchmark',
    value: tooltipValue || 0,
    color: CHART_COLORS.benchmarkColor,
  }

  const renderTooltip = useMemo(
    () => () => createElement(tooltip, { ...data }),
    [tooltip, tooltipValue],
  )

  const handleTooltip = useCallback(
    (event: MouseEvent<SVGLineElement>) =>
      showTooltipFromEvent(renderTooltip(), event),
    [showTooltipFromEvent, renderTooltip],
  )
  const handleMouseEnter = useCallback(
    (event: MouseEvent<SVGLineElement>, accessor: string) => {
      let resolvedTooltipValue = dataProps?.data?.[accessor]

      if (!resolvedTooltipValue) {
        return
      }

      if (dataProps.valueFormatter) {
        resolvedTooltipValue = dataProps.valueFormatter(resolvedTooltipValue)
      }
      setTooltipValue(resolvedTooltipValue)

      const resolvedData = {
        formattedValue: tooltipValue,
        value: tooltipValue,
      }

      onMouseEnter?.({ ...data, ...resolvedData }, event)
      showTooltipFromEvent(renderTooltip(), event)
    },
    [onMouseEnter, showTooltipFromEvent, renderTooltip],
  )
  const handleMouseLeave = useCallback(
    (event: MouseEvent<SVGLineElement>) => {
      onMouseLeave?.(data, event)
      hideTooltip()
    },
    [hideTooltip, onMouseLeave],
  )

  if (layerProps?.bars?.length) {
    return layerProps.bars.map((bar) => {
      try {
        const accessor = dataProps.labels?.[bar.data?.indexValue]
        let value = dataProps.data?.[accessor]

        if (!value) {
          return null
        }

        if (dataProps.valueFormatter) {
          value = dataProps.valueFormatter(value)
        }

        return (
          <g key={bar.key}>
            <line
              x1={bar.x - 10}
              x2={bar.x + bar.width + 10}
              y1={layerProps.yScale(value)}
              y2={layerProps.yScale(value)}
              fill="none"
              stroke={CHART_COLORS.benchmarkColor}
              strokeWidth="3"
              onMouseEnter={
                isInteractive
                  ? (event: MouseEvent<SVGLineElement>) =>
                      handleMouseEnter(event, accessor)
                  : undefined
              }
              onMouseMove={isInteractive ? handleTooltip : undefined}
              onMouseLeave={isInteractive ? handleMouseLeave : undefined}
            />
          </g>
        )
      } catch {
        return null
      }
    })
  }
}

export default BenchmarkHorizontalLayer
